import React, { useState, useRef, useCallback } from 'react';
import { Button, Typography, Tag } from 'antd';
import PropTypes from 'prop-types';
import Map, { NavigationControl, AttributionControl } from 'react-map-gl/mapbox';
import { toPng } from 'html-to-image';
import { ArrowLeftOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useMediaQuery } from 'react-responsive';

import WaterTempIcon from '../../icons/WaterTempIcon';
import { WindSpeedIcon, CurrentIcon } from '../../icons';
import { brandingConfig } from '../../../config';
import SurfaceWaterTemperature from './SurfaceWaterTemperature';
import SurfaceWaterTemperatureMagnitude from './map-overlay-drawer/SurfaceWaterTemperatureMagnitude';
// need to rename default import because it conflicts with the other magnitude components
// eslint-disable-next-line import/no-named-default
import { default as WaterMagnitudeLegend } from './map-overlay-drawer/WaterMagnitude';
import CurrentAnimation from './CurrentAnimation';
// eslint-disable-next-line import/no-named-default
import { default as WindMagnitudeLegend } from './map-overlay-drawer/WindMagnitude';
import WaterMagnitude from './WaterMagnitude';
import WindMagnitude from './WindMagnitude';
import SocialShareSenderModal from '../../../pages/data-console/components/SocialShareSenderModal';
import SeagullLogo from '../../icons/seagullLogo.png';
import { postSharingImage, base64UrlSafeEncode } from '../../../utils/socialSharing';
import { mapStyle } from '../constant';

const { CheckableTag } = Tag;

const overlayTags = [
  {
    label: 'Water Temp',
    id: 'surface_water_temperature',
    color: '#158c96',
    icon: <WaterTempIcon />,
  },
  {
    label: 'Water Current',
    id: 'water_current',
    color: '#1db6c4',
    icon: <CurrentIcon />,
  },
  {
    label: 'Wind',
    id: 'wind_current',
    color: '#7cdf09',
    icon: <WindSpeedIcon />,
  },
];

const MapShareModalContent = (props) => {
  const {
    initialViewport, UTCPath, selectedLake, selectedOverlay, tagsList, onTagSelect, onCancel,
  } = props;
  const adjustedViewport = {
    ...initialViewport,
    zoom: initialViewport.zoom - 1,
    // adjust the zoom a little bit because it appears to be a little too zoomed in
  };
  const [viewport, setViewport] = useState({
    ...adjustedViewport,
  });
  const [checkedTag, setCheckedTag] = useState(overlayTags.find((t) => t.id === selectedOverlay) || overlayTags[0]);
  const [shareImageLink, setShareImageLink] = useState('');
  const [showInfo, setShowInfo] = useState(true);
  const [sharePhase, setSharePhase] = useState(1);
  const [shareImageUrl, setShareImageUrl] = useState('');

  const isMobile = useMediaQuery({ maxWidth: 576 });

  const mapRef = useRef();
  const mapRefLarge = useRef();

  const onViewportChange = (newViewport) => {
    setViewport(newViewport);
    if (showInfo
        && (newViewport.zoom !== adjustedViewport.zoom
          || newViewport.latitude !== adjustedViewport.latitude
          || newViewport.longitude !== adjustedViewport.longitude)) {
      setShowInfo(false);
    }
  };

  const handleTagChange = (tag, checked) => {
    if (tag.id === 'water_current') {
      const tag = tagsList.weatherTags.filter((t) => t.key === 'water_current')[0];
      onTagSelect(tag, true, 'weather');
    }

    if (tag.id === 'wind_current') {
      const tag = tagsList.weatherTags.filter((t) => t.key === 'wind_current')[0];
      onTagSelect(tag, true, 'weather');
    }

    if (tag.id === 'surface_water_temperature') {
      const tag = tagsList.weatherTags.filter((t) => t.key === 'surface_water_temperature')[0];
      onTagSelect(tag, true, 'weather');
    }

    if (checked) {
      setCheckedTag(tag);
    } else {
      setCheckedTag(null);
    }
  };

  const loadImage = (img, src) => new Promise((resolve) => {
    // eslint-disable-next-line no-param-reassign
    img.onload = () => resolve(img);
    // eslint-disable-next-line no-param-reassign
    img.src = src;
  });

  const onDownloadPng = () => {
    const link = document.createElement('a');
    link.download = 'seagull-map.png';
    link.href = shareImageUrl;
    link.click();
  };

  const drawRoundedRect = (context, x, y, width, height, radius) => {
    context.fillStyle = 'black'; // Background color
    context.beginPath();
    context.moveTo(x + radius, y);
    context.lineTo(x + width - radius, y);
    context.arcTo(x + width, y, x + width, y + radius, radius);
    context.lineTo(x + width, y + height - radius);
    context.arcTo(x + width, y + height, x + width - radius, y + height, radius);
    context.lineTo(x + radius, y + height);
    context.arcTo(x, y + height, x, y + height - radius, radius);
    context.lineTo(x, y + radius);
    context.arcTo(x, y, x + radius, y, radius);
    context.closePath();
    context.fill(); // Fill the rectangle
  };

  const onNextClick = () => {
    const legendNode = document.getElementById('sharing-map-overlay-legend');
    toPng(legendNode)
      .then(async (dataUrl) => {
        const map = mapRefLarge.current.getMap();
        const mapCanvas = map.getCanvas();
        const mapDataUrl = mapCanvas.toDataURL('image/png');

        const canvas = document.getElementById('share-map-canvas');
        const context = canvas.getContext('2d');

        canvas.width = 1184;
        canvas.height = 666;

        const mapImage = new Image();
        const legendImage = new Image();
        const logoImage = new Image();

        const imagePromises = [
          loadImage(mapImage, mapDataUrl),
          loadImage(legendImage, dataUrl),
          loadImage(logoImage, SeagullLogo),
        ];

        Promise.all(imagePromises)
          .then((images) => {
            const [mapImg, legendImg, logoImg] = images;

            // const legendOffset = isMobile ? 350 : 270;
            const legendOffset = isMobile ? 260 : 270;

            context.drawImage(mapImg, legendOffset, 0, canvas.width - legendOffset, canvas.height);
            context.drawImage(legendImg, 0, 0, legendOffset, canvas.height);

            const rectWidth = 360;
            const rectHeight = 60;

            const x = canvas.width - 20 - rectWidth; // X coordinate for label background
            const y = canvas.height - 20 - rectHeight; // Y coordinate for label background
            const radius = Math.min(rectWidth, rectHeight) / 2; // Radius for rounded corners

            drawRoundedRect(context, x, y, rectWidth, rectHeight, radius);

            const timestamp = moment().format('MMM D, YYYY h:mma');

            context.font = `24px ${brandingConfig.fonts.bodyName}`;
            context.fillStyle = 'white';
            context.textBaseline = 'middle';
            context.textAlign = 'right';
            context.fillText(timestamp, x + rectWidth / 2 + 100, y + rectHeight / 2);

            context.drawImage(logoImg, canvas.width - 72, canvas.height - 72, 44, 44);

            const combined = canvas.toDataURL();

            postSharingImage(null, combined).then((objectUrl) => {
              if (objectUrl) {
                // TODO: move this object creation to a shared function
                const browserUrl = new URL(window.location.href);
                const s3ObjectTokenId = objectUrl[0];
                const title = checkedTag.label;
                const shareObj = { t: title, i: s3ObjectTokenId };
                browserUrl.searchParams.delete('share');
                browserUrl.searchParams.append('share', base64UrlSafeEncode(JSON.stringify(shareObj)));
                setShareImageLink(browserUrl.href);
              }
            });

            setShareImageUrl(combined);
            setSharePhase(2);
          })
          .catch((error) => {
            console.error('Error loading images', error);
          });
      });
  };

  // TODO: abstract to share with primary CustomMap component
  const EPS = 1e-7;
  const pick = (vs) => ({
    longitude: vs.longitude,
    latitude: vs.latitude,
    zoom: vs.zoom,
    bearing: vs.bearing ?? 0,
    pitch: vs.pitch ?? 0,
  });
  const equalVS = (a, b) => Math.abs(a.longitude - b.longitude) < EPS
    && Math.abs(a.latitude - b.latitude) < EPS
    && Math.abs(a.zoom - b.zoom) < EPS
    && (a.bearing ?? 0) === (b.bearing ?? 0)
    && (a.pitch ?? 0) === (b.pitch ?? 0);

  // Throttle onMove to once per animation frame
  const raf = useRef(0);
  const pending = useRef(viewport);

  const handleMove = useCallback((event) => {
    const next = pick(event.viewState);
    if (equalVS(pending.current, next)) return; // ignore no-op changes
    pending.current = next;

    if (!raf.current) {
      raf.current = requestAnimationFrame(() => {
        setViewport((prev) => (equalVS(prev, pending.current) ? prev : pending.current));
        raf.current = 0;
      });
    }
  }, []);

  if (sharePhase === 2) {
    return (
      <>
        <SocialShareSenderModal
          shareImageUrl={shareImageUrl}
          shareImageLink={shareImageLink}
          onDownloadPng={onDownloadPng}
          isMobile={isMobile}
        />
        <div
          style={{
            borderTop: '1px solid #a3a3a3',
            marginTop: 20,
          }}
        >
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => setSharePhase(1)}
            style={{
              color: 'white',
              background: 'transparent',
              border: 'none',
              fontFamily: brandingConfig.fonts.bodyName,
              fontSize: 16,
              paddingLeft: 0,
              paddingTop: 15,
            }}
          >
            <span style={{ textDecoration: 'underline' }}>
              Back
            </span>
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        style={{
          marginLeft: 3,
          marginRight: 3,
        }}
      >
        <Typography.Title
          level={4}
          style={{
            color: 'white',
            fontFamily: brandingConfig.fonts.headingName,
            fontWeight: 500,
            fontSize: 19,
            paddingRight: 15,
          }}
        >
          Share with your fellow lake enthusiasts
        </Typography.Title>
        <div
          style={{
            marginBottom: 10,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 3,
          }}
        >
          {overlayTags.map((tag) => (
            <CheckableTag
              key={tag.id}
              checked={checkedTag === tag.id}
              onChange={(checked) => handleTagChange(tag, checked)}
              icon={<WaterTempIcon />}
              style={{
                color: 'white',
                border: `1px solid ${tag.color}`,
                fontSize: 14,
                borderRadius: 15,
                background: checkedTag.id === tag.id ? tag.color : 'transparent',
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '3px 6px 3px 4px',
                }}
              >
                {tag.icon}
                {tag.label}
              </span>
            </CheckableTag>
          ))}
        </div>
      </div>
      <div
        style={{
          width: '100%',
          aspectRatio: '16/9',
          borderRadius: 20,
          border: '1px solid #a3a3a3',
          overflow: 'hidden',
          display: 'flex',
        }}
        id="share-map"
      >
        <div
          style={{
            width: isMobile ? 95 : 130,
          }}
          // id="sharing-map-overlay-legend"
        >
          {checkedTag.id === 'surface_water_temperature' && (
            <SurfaceWaterTemperatureMagnitude isActive isSharing />
          )}
          {checkedTag.id === 'water_current' && (
            <WaterMagnitudeLegend isActive isSharing />
          )}
          {checkedTag.id === 'wind_current' && (
            <WindMagnitudeLegend isActive />
          )}
        </div>
        <Map
          viewState={viewport}
          onMove={handleMove}
          ref={mapRef}
          width={460}
          height="100%"
          maxZoom={20}
          maxPitch={0}
          dragRotate={false}
          touchRotate={false}
          mapStyle={mapStyle}
          showZoom
          onViewportChange={onViewportChange}
          attributionControl={false}
          style={{
            borderRadius: 20,
            flex: 1,
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              right: 65,
              background: 'rgba(4, 30, 51, 0.8)',
              color: 'white',
              padding: isMobile ? '10px 7px' : '18px 10px',
              borderRadius: 6,
              maxWidth: 333,
              opacity: showInfo ? 1 : 0,
              transition: 'opacity 0.25s ease-out',
              left: isMobile ? 10 : 'auto',
            }}
          >
            <Typography.Paragraph
              style={{
                color: 'white',
                textAlign: 'center',
                fontSize: isMobile ? 14 : 16,
                marginBottom: 0,
              }}
            >
              Drag around and zoom in to the spot you want to capture on the map
            </Typography.Paragraph>
          </div>
          <NavigationControl
            position="bottom-right"
            showCompass={false}
            className="zoom-controls"
          />
          <AttributionControl position="top-right" compact />
          {checkedTag.id === 'surface_water_temperature' && (
            <SurfaceWaterTemperature
              UTCPath={UTCPath}
              selectedLake={selectedLake}
              isSharing
            />
          )}
          {checkedTag.id === 'water_current' && (
            <>
              <WaterMagnitude
                UTCPath={UTCPath}
                selectedLake={selectedLake}
                isSharing
              />
              <CurrentAnimation
                mapRef={mapRef}
                UTCPath={UTCPath}
                selectedLake={selectedLake}
                selectedOverlay="water_current"
                particleCount={1000}
              />
            </>
          )}
          {checkedTag.id === 'wind_current' && (
            <>
              <WindMagnitude
                UTCPath={UTCPath}
                selectedLake={selectedLake}
                isSharing
              />
              <CurrentAnimation
                mapRef={mapRef}
                UTCPath={UTCPath}
                selectedLake={selectedLake}
                selectedOverlay="wind"
                particleCount={1000}
              />
            </>
          )}
        </Map>
      </div>

      <div
        style={{
          position: 'absolute',
          opacity: 0,
          zIndex: -9999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: isMobile ? 210 : 270,
            height: isMobile ? 570 : 660,
            transform: 'scale(2)',
            transformOrigin: 'top left',
          }}
          id="sharing-map-overlay-legend"
        >
          {checkedTag.id === 'surface_water_temperature' && (
            <SurfaceWaterTemperatureMagnitude isActive isSharing />
          )}
          {checkedTag.id === 'water_current' && (
            <WaterMagnitudeLegend isActive isSharing />
          )}
          {checkedTag.id === 'wind_current' && (
            <WindMagnitudeLegend isActive />
          )}
        </div>
      </div>
      <div
        style={{
          width: '200%',
          aspectRatio: '16/9',
          borderRadius: 20,
          border: '1px solid #a3a3a3',
          overflow: 'visible',
          display: 'none',
        }}
        id="share-map-large"
      >
        <Map
          viewState={viewport}
          onMove={handleMove}
          zoom={isMobile ? viewport.zoom + 0.6 : viewport.zoom - 0.2}
          ref={mapRefLarge}
          // width={920}
          width={isMobile ? 900 : 920}
          height="100%"
          touchRotate={false}
          mapStyle={mapStyle}
          preserveDrawingBuffer
        >
          <NavigationControl
            style={{
              position: 'absolute',
              right: 10,
              bottom: 10,
            }}
            showCompass={false}
            className="zoom-controls"
          />
          {checkedTag.id === 'surface_water_temperature' && (
            <SurfaceWaterTemperature
              UTCPath={UTCPath}
              selectedLake={selectedLake}
              isSharing
            />
          )}
          {checkedTag.id === 'water_current' && (
            <>
              <WaterMagnitude
                UTCPath={UTCPath}
                selectedLake={selectedLake}
                isSharing
              />
              <CurrentAnimation
                mapRef={mapRefLarge}
                UTCPath={UTCPath}
                selectedLake={selectedLake}
                selectedOverlay="water_current"
                particleCount={1000}
              />
            </>
          )}
          {checkedTag.id === 'wind_current' && (
            <>
              <WindMagnitude
                UTCPath={UTCPath}
                selectedLake={selectedLake}
                isSharing
              />
              <CurrentAnimation
                mapRef={mapRefLarge}
                UTCPath={UTCPath}
                selectedLake={selectedLake}
                selectedOverlay="wind"
                particleCount={1000}
              />
            </>
          )}
        </Map>
      </div>

      <canvas id="share-map-canvas" style={{ display: 'none' }} />
      <div
        style={{
          borderTop: '1px solid #a3a3a3',
          marginTop: 20,
          paddingTop: 20,
          marginLeft: 3,
          marginRight: 3,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 15,
        }}
      >
        <Button
          onClick={onCancel}
          style={{
            color: 'white',
            background: 'transparent',
            border: 'none',
            textDecoration: 'underline',
            fontFamily: brandingConfig.fonts.bodyName,
            fontSize: 16,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onNextClick}
          style={{
            color: 'white',
            border: 'none',
            background: brandingConfig.colors.accent1,
            fontFamily: brandingConfig.fonts.headingName,
            fontSize: 16,
            borderRadius: 6,
            height: 'auto',
            padding: '8px 30px',
          }}
        >
          Next
        </Button>
      </div>
    </>
  );
};

MapShareModalContent.propTypes = {
  initialViewport: PropTypes.object.isRequired,
  UTCPath: PropTypes.string,
  selectedLake: PropTypes.string,
  selectedOverlay: PropTypes.string,
  tagsList: PropTypes.array,
  onTagSelect: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
};

MapShareModalContent.defaultProps = {
  UTCPath: '',
  selectedLake: '',
  selectedOverlay: '',
  tagsList: [],
  onTagSelect: () => {},
};

export default MapShareModalContent;
